// Build-command UI: hotkeys 1..5 select an action (Farm/Road/Building/Pillbox/Mine);
// while an action is "armed" the cursor shows a target highlight on the hovered
// tile, and a click issues the build command to the local player's LGM.
//
// Architecture: this module is a small state machine that maintains "armed
// action" + "hover tile" and exposes:
//   - draw() — paints the highlight on top of the renderer's output
//   - tryEmit() — returns a one-shot BuildCommand to send if the user clicked
//     while armed, then clears it.

import { BuildAction, type BuildActionCode } from '@bolo2k/sim';
import { MAP_SIZE, WORLD_UNITS_PER_TILE } from '@bolo2k/shared';
import { TILE_PX } from '@bolo2k/client-core';

// User-facing actions: 5 only. No Boat keybinding — the client auto-substitutes
// Wall (Building) clicked on a River tile to a Boat job server-side, matching
// Cheshire/WinBolo (lgm.c:386-394). See main.ts autoSubstituteAction.
const ACTION_BY_KEY: Record<string, BuildActionCode> = {
  Digit1: BuildAction.Farm,
  Digit2: BuildAction.Road,
  Digit3: BuildAction.Building,
  Digit4: BuildAction.Pillbox,
  Digit5: BuildAction.Mine,
};

const ACTION_LABEL: Record<BuildActionCode, string> = {
  [BuildAction.Farm]: 'farm (chop tree)',
  [BuildAction.Road]: 'road',
  [BuildAction.Building]: 'wall',
  [BuildAction.Pillbox]: 'pillbox',
  [BuildAction.Mine]: 'mine',
  [BuildAction.Boat]: 'boat (auto via wall + river)', // never user-armed
};

export class BuildUI {
  // Radio-button semantics: exactly one action is always armed. Default is
  // Farm — chops trees on the hovered tile, the safest default since trees
  // grow back. Player switches modes via 1..5 hotkeys or click-to-arm in
  // the left-column action sidebar; there is no "no action armed" state.
  private armedAction: BuildActionCode = BuildAction.Farm;
  private hoverTile: { x: number; y: number } | null = null;
  private pending: { action: BuildActionCode; targetX: number; targetY: number } | null = null;
  // Set by main.ts whenever the camera moves so we can convert mouse → world.
  private camTileX = 0;
  private camTileY = 0;
  // Cached canvas bounding rect — getBoundingClientRect() forces a synchronous
  // layout, so reading it from every mousemove (60+ Hz) burned CPU and could
  // stall the render loop. Refreshed only when something would actually move
  // the canvas: ResizeObserver for size changes, window scroll/resize for
  // viewport-relative shifts. Stays in sync without per-event reads.
  private cachedRect: DOMRect;
  // Status-line element so we can show the armed action label.
  constructor(
    private canvas: HTMLCanvasElement,
    private statusEl: HTMLElement | null,
  ) {
    this.cachedRect = canvas.getBoundingClientRect();
    const refreshRect = (): void => {
      this.cachedRect = this.canvas.getBoundingClientRect();
    };
    new ResizeObserver(refreshRect).observe(canvas);
    window.addEventListener('scroll', refreshRect, { passive: true });
    window.addEventListener('resize', refreshRect);
    this.attach();
  }

  setCamera(camTileX: number, camTileY: number): void {
    this.camTileX = camTileX;
    this.camTileY = camTileY;
  }

  // Read-only access for the action-sidebar's "which cell is armed" display.
  get armed(): BuildActionCode {
    return this.armedAction;
  }

  /** Switch the armed action. Used by the left-column click-to-arm path
   *  (keyboard 1..5 go through the same state via the keydown handler).
   *  Idempotent — re-arming the same action is a no-op. Drops any queued
   *  click so an action change doesn't fire the previous action's pending
   *  click once the LGM frees up. */
  armAction(action: BuildActionCode): void {
    if (this.armedAction === action) return;
    this.armedAction = action;
    this.pending = null;
    this.refreshStatus();
  }

  /** True when there's an unsent click queued — used by the caller to defer
   *  send while the LGM is busy. The pending command stays buffered
   *  (subsequent clicks overwrite it; latest-click-wins) until tryEmit() is
   *  called by a caller that's confirmed the LGM is free. */
  hasPending(): boolean {
    return this.pending !== null;
  }

  // Returns a one-shot build command and clears it. Caller sends to server.
  tryEmit(): { action: BuildActionCode; targetX: number; targetY: number } | null {
    const out = this.pending;
    this.pending = null;
    return out;
  }

  /** Clear any queued click without consuming it. Used when the player
   *  changes mode or aborts. */
  clearPending(): void {
    this.pending = null;
  }

  draw(ctx: CanvasRenderingContext2D): void {
    if (!this.hoverTile) return;
    const sx = (this.hoverTile.x - this.camTileX) * TILE_PX;
    const sy = (this.hoverTile.y - this.camTileY) * TILE_PX;
    ctx.save();
    ctx.strokeStyle = '#ffdf5d';
    ctx.lineWidth = 2;
    ctx.setLineDash([4, 3]);
    ctx.strokeRect(sx + 1, sy + 1, TILE_PX - 2, TILE_PX - 2);
    ctx.restore();
  }

  private attach(): void {
    window.addEventListener('keydown', (e) => {
      const action = ACTION_BY_KEY[e.code];
      if (action !== undefined) {
        if (this.armedAction !== action) {
          this.armedAction = action;
          // Drop any queued click — the user just switched intent.
          this.pending = null;
          this.refreshStatus();
        }
        e.preventDefault();
      }
    });
    this.canvas.addEventListener('mousemove', (e) => {
      this.hoverTile = this.tileAtClient(e.clientX, e.clientY);
    });
    this.canvas.addEventListener('mouseleave', () => {
      this.hoverTile = null;
    });
    // pointerdown fires on press (not release) and doesn't require mouseup
    // on the same element — fixes the "first click sometimes does nothing"
    // bug where a tiny drag between mousedown/mouseup swallowed the click.
    this.canvas.addEventListener('pointerdown', (e) => {
      if (e.button !== 0) return; // left button only
      const tile = this.tileAtClient(e.clientX, e.clientY);
      if (!tile) return;
      this.pending = { action: this.armedAction, targetX: tile.x, targetY: tile.y };
      // Action stays armed across clicks — radio-button semantics; switch
      // modes via 1..5 hotkeys or click another action in the sidebar. Clicks
      // on tiles where the action doesn't apply (Farm on grass, Road on
      // building, etc.) are silently rejected by reserveJob; the yellow
      // target highlight makes it visible that the action is still armed.
    });
  }

  private tileAtClient(clientX: number, clientY: number): { x: number; y: number } | null {
    const rect = this.cachedRect;
    const screenX = clientX - rect.left;
    const screenY = clientY - rect.top;
    const tx = Math.floor(this.camTileX + screenX / TILE_PX);
    const ty = Math.floor(this.camTileY + screenY / TILE_PX);
    if (tx < 0 || ty < 0 || tx >= MAP_SIZE || ty >= MAP_SIZE) return null;
    return { x: tx, y: ty };
  }

  private refreshStatus(): void {
    if (!this.statusEl) return;
    this.statusEl.textContent = `armed: ${ACTION_LABEL[this.armedAction]} — click a tile`;
    this.statusEl.classList.remove('warn', 'error');
  }
}

void WORLD_UNITS_PER_TILE; // referenced for documentation
